import { clubDaysUntil, clubNowIn } from '~/shared/lib/clubClock';
import { clubDateOf, formatClubDate, formatClubTime } from '~/shared/lib/clubZone';

type LocaleCode = 'ru-RU' | 'en-US';

const toBcp47 = (locale: 'ru' | 'en'): LocaleCode => (locale === 'en' ? 'en-US' : 'ru-RU');

const moneyCache = new Map<string, Intl.NumberFormat>();
const money = (locale: LocaleCode, currency = 'RUB'): Intl.NumberFormat => {
  const key = `${locale}:${currency}`;
  let fmt = moneyCache.get(key);
  if (!fmt) {
    fmt = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      maximumFractionDigits: 0,
    });
    moneyCache.set(key, fmt);
  }
  return fmt;
};

export const formatMoney = (
  amount: number,
  opts: { locale?: 'ru' | 'en'; currency?: string } = {},
): string => {
  const locale = toBcp47(opts.locale ?? 'ru');
  if (!Number.isFinite(amount)) return '—';
  return money(locale, opts.currency ?? 'RUB').format(amount);
};

export const formatKopecks = (
  amountKopecks: number | null | undefined,
  opts: { locale?: 'ru' | 'en'; currency?: string; exact?: boolean } = {},
): string => {
  if (amountKopecks == null) return '—';
  if (opts.exact && Number.isFinite(amountKopecks) && amountKopecks % 100 !== 0) {
    return exactMoney(toBcp47(opts.locale ?? 'ru'), opts.currency ?? 'RUB').format(amountKopecks / 100);
  }
  return formatMoney(amountKopecks / 100, opts);
};

const exactMoneyCache = new Map<string, Intl.NumberFormat>();
const exactMoney = (locale: LocaleCode, currency: string): Intl.NumberFormat => {
  const key = `${locale}:${currency}`;
  let fmt = exactMoneyCache.get(key);
  if (!fmt) {
    fmt = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    exactMoneyCache.set(key, fmt);
  }
  return fmt;
};

export const formatKopecksCompact = (amountKopecks: number | null | undefined): string => {
  if (amountKopecks == null || !Number.isFinite(amountKopecks)) return '—';
  const rub = Math.round(amountKopecks / 100);
  if (Math.abs(rub) >= 1_000_000) return `${formatNumber(rub / 1_000_000, { maximumFractionDigits: 1 })} млн ₽`;
  if (Math.abs(rub) >= 10_000) return `${formatNumber(Math.round(rub / 1_000))} тыс ₽`;
  return formatMoney(rub);
};

const numberCache = new Map<string, Intl.NumberFormat>();
export const formatNumber = (
  value: number,
  opts: { locale?: 'ru' | 'en'; maximumFractionDigits?: number } = {},
): string => {
  const locale = toBcp47(opts.locale ?? 'ru');
  const key = `${locale}:${opts.maximumFractionDigits ?? 0}`;
  let fmt = numberCache.get(key);
  if (!fmt) {
    fmt = new Intl.NumberFormat(locale, {
      maximumFractionDigits: opts.maximumFractionDigits ?? 0,
    });
    numberCache.set(key, fmt);
  }
  return fmt.format(value);
};

export const pluralRu = (n: number, one: string, few: string, many: string): string => {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return one;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return few;
  return many;
};

export const formatBonuses = (kopecks: number | null | undefined): string => {
  if (kopecks == null || !Number.isFinite(kopecks)) return '—';
  const abs = Math.abs(kopecks);
  if (abs % 100 !== 0) {
    return `${(abs / 100).toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} бонуса`;
  }
  const whole = abs / 100;
  return `${formatNumber(whole)} ${pluralRu(whole, 'бонус', 'бонуса', 'бонусов')}`;
};

export const formatPercent = (
  fraction: number,
  opts: { locale?: 'ru' | 'en'; fractionDigits?: number } = {},
): string => {
  const locale = toBcp47(opts.locale ?? 'ru');
  return new Intl.NumberFormat(locale, {
    style: 'percent',
    maximumFractionDigits: opts.fractionDigits ?? 0,
  }).format(fraction);
};

const dateCache = new Map<string, Intl.DateTimeFormat>();
const dateFmt = (locale: LocaleCode, options: Intl.DateTimeFormatOptions, timeZone: string): Intl.DateTimeFormat => {
  const key = `${locale}:${timeZone}:${JSON.stringify(options)}`;
  let fmt = dateCache.get(key);
  if (!fmt) {
    fmt = new Intl.DateTimeFormat(locale, { ...options, timeZone });
    dateCache.set(key, fmt);
  }
  return fmt;
};

export const formatDateOnly = (
  value: string,
  opts: {
    locale?: 'ru' | 'en';
    preset?: 'short' | 'medium' | 'long' | 'weekday' | 'dayMonth' | 'dayMonthYear';
  } = {},
): string => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (!m) return value;
  const at = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
  if (Number.isNaN(at.getTime())) return value;
  const locale = toBcp47(opts.locale ?? 'ru');
  const preset = opts.preset ?? 'short';
  const options: Intl.DateTimeFormatOptions =
    preset === 'short'
      ? { day: '2-digit', month: '2-digit', year: 'numeric' }
      : preset === 'medium'
        ? { day: '2-digit', month: 'long', year: 'numeric' }
        : preset === 'long'
          ? { day: '2-digit', month: 'long', year: 'numeric', weekday: 'long' }
          : preset === 'weekday'
            ? { weekday: 'short' }
            : preset === 'dayMonthYear'
              ? { day: 'numeric', month: 'long', year: 'numeric' }
              : { day: '2-digit', month: 'short' };
  return dateFmt(locale, options, 'UTC').format(at);
};

const UNITS: Array<{ limit: number; divisor: number; unit: Intl.RelativeTimeFormatUnit }> = [
  { limit: 60, divisor: 1, unit: 'second' },
  { limit: 3_600, divisor: 60, unit: 'minute' },
  { limit: 86_400, divisor: 3_600, unit: 'hour' },
  { limit: 604_800, divisor: 86_400, unit: 'day' },
  { limit: 2_592_000, divisor: 604_800, unit: 'week' },
  { limit: 31_536_000, divisor: 2_592_000, unit: 'month' },
  { limit: Number.POSITIVE_INFINITY, divisor: 31_536_000, unit: 'year' },
];

export const formatRelativeTime = (
  from: Date | string | number,
  opts: { locale?: 'ru' | 'en'; now?: Date } = {},
): string => {
  const locale = toBcp47(opts.locale ?? 'ru');
  const now = opts.now ?? new Date();
  const then = typeof from === 'object' ? from : new Date(from);
  const diffSec = Math.round((then.getTime() - now.getTime()) / 1000);
  const abs = Math.abs(diffSec);
  const bucket = UNITS.find((u) => abs < u.limit) ?? UNITS[UNITS.length - 1]!;
  const value = Math.round(diffSec / bucket.divisor);
  return new Intl.RelativeTimeFormat(locale, { numeric: 'auto' }).format(value, bucket.unit);
};

export const compactRelativeDuration = (iso: string, now: number = Date.now()): string => {
  const ms = now - new Date(iso).getTime();
  if (ms < 60_000) return 'только что';
  if (ms < 3_600_000) return `${Math.floor(ms / 60_000)} мин назад`;
  if (ms < 86_400_000) return `${Math.floor(ms / 3_600_000)} ч назад`;
  return `${Math.floor(ms / 86_400_000)} дн назад`;
};

export const formatPhoneRu = (digits: string): string => {
  const d = digits.replace(/\D/g, '').replace(/^7/, '').slice(0, 10);
  const parts: string[] = [];
  if (d.length > 0) parts.push(d.slice(0, 3));
  if (d.length > 3) parts.push(d.slice(3, 6));
  if (d.length > 6) parts.push(`${d.slice(6, 8)}-${d.slice(8, 10)}`);
  const body = parts.filter(Boolean).join(' ');
  return body ? `+7 ${body}` : '';
};

export const formatDurationMin = (minutes: number, locale: 'ru' | 'en' = 'ru'): string => {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (locale === 'en') {
    if (h && m) return `${h}h ${m}m`;
    if (h) return `${h}h`;
    return `${m}m`;
  }
  if (h && m) return `${h} ч ${m} мин`;
  if (h) return `${h} ч`;
  return `${m} мин`;
};

export const initials = (name: string | null | undefined): string => {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0]?.toUpperCase() ?? '').join('') || '?';
};

export const formatRelativeVisit = (iso: string | null | undefined, zone: string): string => {
  if (!iso) return '—';
  const days = Math.max(0, -clubDaysUntil(clubDateOf(iso, zone), clubNowIn(zone)));
  if (days === 0) return 'сегодня';
  if (days === 1) return 'вчера';
  if (days < 7) return `${days} дн. назад`;
  if (days < 30) return `${Math.floor(days / 7)} нед. назад`;
  return formatClubDate(iso, zone, 'short');
};

export const formatDateTimeShort = (iso: string, zone: string): string => {
  if (Number.isNaN(Date.parse(iso))) return iso;
  const day = clubDateOf(iso, zone);
  return `${day.slice(8, 10)}.${day.slice(5, 7)} ${formatClubTime(iso, zone)}`;
};

export const percentFloorKopecks = (amountKopecks: number, pct: number): number =>
  Math.floor((amountKopecks * pct) / 100);
